import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

async function invoke(action: string, params: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke("legacy-notes", {
    body: { action, ...params },
  });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
  return data;
}

export function useOldMemberStatus() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["old-member-status", user?.id],
    queryFn: () => invoke("get_profile_status"),
    enabled: !!user,
    staleTime: 30_000,
  });
}

export function useMyLegacyNotes() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["my-legacy-notes", user?.id],
    queryFn: () => invoke("get_my_notes"),
    enabled: !!user,
  });
}

export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ["note-subscription-plans"],
    queryFn: () => invoke("get_subscription_plans"),
  });
}

export function useMarketListings(filters?: { asset?: string; min_price?: number; max_price?: number }) {
  return useQuery({
    queryKey: ["notes-market-listings", filters],
    queryFn: () => invoke("get_market_listings", filters || {}),
  });
}

export function useRequestActivation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => invoke("request_activation"),
    onSuccess: () => {
      toast.success("Activation request submitted");
      qc.invalidateQueries({ queryKey: ["old-member-status"] });
      qc.invalidateQueries({ queryKey: ["my-legacy-notes"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function usePaySubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (plan_id: string) => invoke("pay_subscription", { plan_id }),
    onSuccess: () => {
      toast.success("Subscription activated");
      qc.invalidateQueries({ queryKey: ["my-legacy-notes"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useListNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { note_id: string; asking_price: number; currency?: string }) =>
      invoke("list_note_for_sale", params),
    onSuccess: () => {
      toast.success("Note listed for sale");
      qc.invalidateQueries({ queryKey: ["my-legacy-notes"] });
      qc.invalidateQueries({ queryKey: ["notes-market-listings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useCancelListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (listing_id: string) => invoke("cancel_listing", { listing_id }),
    onSuccess: () => {
      toast.success("Listing cancelled");
      qc.invalidateQueries({ queryKey: ["my-legacy-notes"] });
      qc.invalidateQueries({ queryKey: ["notes-market-listings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useBuyNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (listing_id: string) => invoke("buy_note", { listing_id }),
    onSuccess: () => {
      toast.success("Note purchased successfully");
      qc.invalidateQueries({ queryKey: ["my-legacy-notes"] });
      qc.invalidateQueries({ queryKey: ["notes-market-listings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// ── Admin hooks ──
export function useAdminImportNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { user_id: string; notes: any[] }) => invoke("import_notes", params),
    onSuccess: () => {
      toast.success("Notes imported");
      qc.invalidateQueries({ queryKey: ["admin-all-notes"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useAdminActivationQueue() {
  return useQuery({
    queryKey: ["admin-activation-queue"],
    queryFn: () => invoke("get_activation_queue"),
  });
}

export function useAdminApproveActivation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (user_id: string) => invoke("approve_activation", { user_id }),
    onSuccess: () => {
      toast.success("Activation approved");
      qc.invalidateQueries({ queryKey: ["admin-activation-queue"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useAdminRejectActivation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (user_id: string) => invoke("reject_activation", { user_id }),
    onSuccess: () => {
      toast.success("Activation rejected");
      qc.invalidateQueries({ queryKey: ["admin-activation-queue"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useAdminAllNotes() {
  return useQuery({
    queryKey: ["admin-all-notes"],
    queryFn: () => invoke("get_all_notes"),
  });
}

export function useAdminLockNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note_id: string) => invoke("lock_note", { note_id }),
    onSuccess: () => {
      toast.success("Note locked");
      qc.invalidateQueries({ queryKey: ["admin-all-notes"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useAdminUnlockNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note_id: string) => invoke("unlock_note", { note_id }),
    onSuccess: () => {
      toast.success("Note unlocked");
      qc.invalidateQueries({ queryKey: ["admin-all-notes"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useAdminManagePlans() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { operation: string; plan: any }) =>
      invoke("manage_subscription_plans", params),
    onSuccess: () => {
      toast.success("Plan updated");
      qc.invalidateQueries({ queryKey: ["admin-all-plans"] });
      qc.invalidateQueries({ queryKey: ["note-subscription-plans"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useAdminAllPlans() {
  return useQuery({
    queryKey: ["admin-all-plans"],
    queryFn: () => invoke("get_all_plans"),
  });
}

export function useAdminAllSubscriptions() {
  return useQuery({
    queryKey: ["admin-all-subscriptions"],
    queryFn: () => invoke("get_all_subscriptions"),
  });
}
